import { Injectable, inject, signal, NgZone, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Translation, TranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Simple HTTP loader for Transloco JSON translation files.
 * Returns the appropriate asset for the requested language.
 */
@Injectable({ providedIn: 'root' })
export class HttpLoader implements TranslocoLoader {
  constructor(private http: HttpClient) {}
  getTranslation(lang: string): Observable<Translation> {
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

/**
 * Manages the active application language:
 *  - Bridges Transloco's RxJS langChanges$ to an Angular Signal
 *  - Detects language from localStorage, user locale, or browser preference
 *  - Updates document direction & arabic-font class
 *  - Persists the chosen locale to the backend (non‑blocking, auth‑gated)
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translocoService = inject(TranslocoService);
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);
  private storageKey = 'locale';

  // Universal Signal Bridge
  private _currentLang = signal<string>('en');
  readonly currentLanguage = this._currentLang.asReadonly();
  readonly isInitialized = signal<boolean>(false);

  constructor() {
    // Bridge Transloco's Observable to an Angular Signal
    this.translocoService.langChanges$.subscribe(lang => {
      this._currentLang.set(lang);
      this.ngZone.run(() => this.updateDocumentDirection(lang));
    });
  }

  /**
   * Initialise the locale on application startup.
   * @param userLocale - Optional locale from backend (e.g., user preferences)
   */
  async initLocale(userLocale?: string): Promise<void> {
    const savedLang = userLocale || localStorage.getItem(this.storageKey) || 'en';
    this.translocoService.setActiveLang(savedLang);
    this._currentLang.set(savedLang); // ensure immediate signal update
    this.isInitialized.set(true);
  }

  /**
   * Change the active language at runtime.
   * Persists to localStorage, updates Transloco (which triggers the subscription
   * to update the signal and document direction), and syncs to the backend
   * **only when the user is authenticated**.
   *
   * No-ops when the requested language is already the active Transloco language.
   */
  async setLanguage(lang: string): Promise<void> {
    if (!['en', 'fr', 'es', 'ar'].includes(lang)) {
      lang = 'en';
    }
    // Guard against redundant calls that may cause flickers
    if (lang === this.translocoService.getActiveLang()) {
      return;
    }

    localStorage.setItem(this.storageKey, lang);
    this.translocoService.setActiveLang(lang);

    // Fire-and-forget backend sync – never blocks the UI
    this.syncLocaleToBackend(lang);
  }

  private updateDocumentDirection(lang: string): void {
    document.documentElement.lang = lang;
    const isRtl = lang === 'ar';
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.body.classList.toggle('arabic-font', isRtl);
  }

  /**
   * Sends the current locale to the backend **only if the user is authenticated**.
   * Failures are caught and logged but NEVER throw.
   */
  private syncLocaleToBackend(locale: string): void {
    // Lazily resolve AuthService to break the circular dependency chain.
    const authService = this.injector.get(AuthService);
    // Guard: do not attempt to update locale for unauthenticated (guest) users
    if (!authService.isLoggedIn()) {
      return;
    }
    try {
      this.http.patch('/users/me/locale', { locale }).subscribe({
        error: (err) => console.error('Failed to update locale on backend', err)
      });
    } catch (err) {
      console.error('Failed to send locale update to backend', err);
    }
  }
}
